import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { formatPence } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { loadStaff, saveStaff, type StaffSession } from '../lib/session.js';

interface EventOption {
  id: string;
  name: string;
  status: string;
  scheduledFor: string | null;
}

interface RunSheet {
  /** Canonical, from the server -- never this browser's origin. See runsheet.ts. */
  playerUrl: string;
  eventId: string;
  eventName: string;
  quizName: string;
  joinMode: 'TICKET' | 'CODE' | 'OPEN';
  entryFeePence: number;
  isFree: boolean;
  joinCode: string | null;
  timeLimitSec: number;
  playerCount: number;
  totalQuestions: number;
  rounds: {
    title: string;
    orderIndex: number;
    questionCount: number;
    pointsMultiplierPct: number;
    timeLimitSec: number | null;
  }[];
}

/**
 * The paper for the night: what to pin up, what to put on the tables, and the
 * running order for whoever is holding the microphone.
 *
 * Driven from the server rather than typed into a document, because the URL, the
 * join code and the running order all change between nights -- and a printout
 * that has quietly gone stale is worse than no printout at all.
 *
 * Light-themed regardless of the rest of the app: this one is for paper.
 */
export function RunSheet() {
  const [staff, setStaff] = useState<StaffSession | null>(loadStaff);
  const [sheet, setSheet] = useState<RunSheet | null>(null);

  /**
   * An admin can print ANY night, and that is the point of the picker:
   * preparing next Thursday's paperwork in advance is the normal case, and its
   * manager PIN may not have been handed to anybody yet. A manager signing in
   * with a PIN still gets their own night and only their own night.
   */
  const [adminToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem('fast-quiz/admin-token');
    } catch {
      return null;
    }
  });
  const [events, setEvents] = useState<EventOption[] | null>(null);
  const [chosen, setChosen] = useState<string>(
    () => new URLSearchParams(location.search).get('event') ?? '',
  );
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pin, setPin] = useState('');

  const origin = window.location.origin;
  // The server names the URL the room should type. Falling back to this
  // browser's origin only matters before the sheet has loaded.
  const playerUrl = sheet?.playerUrl || origin;

  // The list of nights to choose between, for an admin only.
  useEffect(() => {
    if (!adminToken) return;
    api<{ events: EventOption[] }>('/api/admin/events', { token: adminToken })
      .then((r) => {
        setEvents(r.events);
        setChosen((c) => c || r.events[0]?.id || '');
      })
      .catch(() => setEvents([]));
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      if (!chosen) return;
      api<RunSheet>(`/api/admin/runsheet?eventId=${encodeURIComponent(chosen)}`, {
        token: adminToken,
      })
        .then(setSheet)
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load.'));
      return;
    }
    if (!staff) return;
    api<RunSheet>('/api/staff/runsheet', { token: staff.token })
      .then(setSheet)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load.'));
  }, [staff, adminToken, chosen]);

  useEffect(() => {
    // Big and high-correction, because this gets scanned across a dark table.
    QRCode.toDataURL(playerUrl, { width: 640, margin: 1, errorCorrectionLevel: 'H' })
      .then(setQr)
      .catch(() => setQr(null));
  }, [playerUrl]);

  const signIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      try {
        const res = await api<{
          token: string;
          eventId: string;
          staff: { displayName: string; role: 'HOST' | 'MANAGER' };
        }>('/api/staff/login', { method: 'POST', body: { pin } });
        const session: StaffSession = {
          token: res.token,
          displayName: res.staff.displayName,
          role: res.staff.role,
          eventId: res.eventId,
        };
        saveStaff(session);
        setStaff(session);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not sign in.');
      }
    },
    [pin],
  );

  if ((!staff && !adminToken) || !sheet) {
    return (
      <Paper>
        <h1 className="text-2xl font-bold">Run sheet</h1>
        <p className="mt-2 text-neutral-600">
          Sign in with a host or manager PIN to print the night&rsquo;s paperwork.
        </p>
        <form onSubmit={signIn} className="mt-6 flex gap-2">
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            placeholder="PIN"
            className="w-40 rounded border border-neutral-400 px-3 py-2 text-lg"
          />
          <button
            type="submit"
            disabled={pin.length < 4}
            className="rounded bg-neutral-900 px-5 py-2 font-semibold text-white disabled:opacity-40"
          >
            Open
          </button>
        </form>
        {error && <p className="mt-3 text-red-700">{error}</p>}
      </Paper>
    );
  }

  const howToJoin = {
    OPEN: 'Scan the code, type a name, tap join. Nothing else.',
    CODE: `Scan the code, enter ${sheet.joinCode}, type a name.`,
    TICKET: 'Scan the code, enter the ticket code from the door, type a name.',
  }[sheet.joinMode];

  return (
    <Paper>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3 rounded bg-neutral-100 p-3">
        {events && events.length > 0 ? (
          <label className="flex items-center gap-2 text-sm text-neutral-600">
            Night
            <select
              value={chosen}
              onChange={(e) => {
                setChosen(e.target.value);
                setSheet(null);
              }}
              className="rounded border border-neutral-400 bg-white px-2 py-1.5 text-sm"
            >
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                  {e.scheduledFor ? ` — ${new Date(e.scheduledFor).toLocaleDateString()}` : ''}
                  {` (${e.status.toLowerCase()})`}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="text-sm text-neutral-600">
            Printed as three pages: table card, host sheet, door sheet.
          </p>
        )}
        <button
          onClick={() => window.print()}
          className="rounded bg-neutral-900 px-4 py-2 text-sm font-semibold text-white"
        >
          Print
        </button>
      </div>

      {/* ---- Page 1: the table card ---- */}
      <section className="page">
        <p className="text-sm uppercase tracking-widest text-neutral-500">Join the quiz</p>
        <h1 className="mt-1 text-4xl font-bold">{sheet.eventName}</h1>

        {qr && (
          <img
            src={qr}
            alt={`QR code linking to ${playerUrl}`}
            className="mx-auto my-8 h-72 w-72"
          />
        )}

        <p className="text-center text-2xl font-semibold break-all">{stripScheme(playerUrl)}</p>

        {sheet.joinMode === 'CODE' && (
          <p className="mt-6 text-center">
            <span className="text-sm uppercase tracking-widest text-neutral-500">Join code</span>
            <br />
            <span className="font-mono text-5xl font-bold tracking-[0.2em]">{sheet.joinCode}</span>
          </p>
        )}
        {sheet.joinMode === 'OPEN' && (
          <p className="mt-6 text-center text-xl">No code needed &mdash; just tap join.</p>
        )}
        {sheet.joinMode === 'TICKET' && (
          <p className="mt-6 text-center text-xl">
            {formatPence(sheet.entryFeePence)} at the door. You&rsquo;ll be given a code.
          </p>
        )}

        <p className="mt-8 text-center text-neutral-600">
          One phone per person. Keep the page open &mdash; the questions appear on it.
        </p>
      </section>

      {/* ---- Page 2: the host's sheet ---- */}
      <section className="page">
        <h2 className="text-3xl font-bold">Host sheet</h2>
        <p className="mt-1 text-neutral-600">
          {sheet.quizName} &middot; {sheet.rounds.length} rounds &middot; {sheet.totalQuestions}{' '}
          questions &middot; {sheet.timeLimitSec}s per question
        </p>

        <Field label="Your screen" value={`${stripScheme(playerUrl)}/staff`} />
        <Field label="Host PIN" value="" note="write it in — it is not stored in readable form" />

        <h3 className="mt-8 text-lg font-bold">Running order</h3>
        <table className="mt-2 w-full text-left">
          <thead>
            <tr className="border-b border-neutral-300 text-sm text-neutral-600">
              <th className="py-1">#</th>
              <th>Round</th>
              <th className="text-right">Questions</th>
              <th className="text-right">Points</th>
            </tr>
          </thead>
          <tbody>
            {sheet.rounds.map((r) => (
              <tr key={r.orderIndex} className="border-b border-neutral-200">
                <td className="py-2">{r.orderIndex + 1}</td>
                <td className="font-medium">{r.title}</td>
                <td className="text-right">{r.questionCount}</td>
                <td className="text-right">
                  {r.pointsMultiplierPct === 100 ? 'normal' : `${r.pointsMultiplierPct}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="mt-8 text-lg font-bold">How each question goes</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li><b>Start round</b> &mdash; names the round, stages the first question.</li>
          <li>Read the question out. Nobody can see it yet.</li>
          <li><b>Open</b> &mdash; it appears on every phone and the clock starts.</li>
          <li><b>Close</b> when the clock runs out or everyone has answered.</li>
          <li><b>Reveal</b> &mdash; read the answer out as it appears on their phones.</li>
          <li><b>Next</b>. At the end of a round you get a break screen to talk over.</li>
        </ol>
        <p className="mt-3 text-sm text-neutral-600">
          Space advances, R reveals. A wrong answer marked wrong can be flipped with
          the tick beside it &mdash; the player&rsquo;s score corrects itself within
          two seconds.
        </p>
        <p className="mt-2 text-sm text-neutral-600">
          Finish with <b>End the quiz</b>. Everyone sees the final leaderboard, and so do you.
        </p>
      </section>

      {/* ---- Page 3: the door ---- */}
      <section className="page">
        <h2 className="text-3xl font-bold">Door sheet</h2>
        <p className="mt-1 text-neutral-600">
          {sheet.isFree ? 'Free entry tonight.' : `Entry ${formatPence(sheet.entryFeePence)}.`}
        </p>

        <Field label="Your screen" value={`${stripScheme(playerUrl)}/staff`} />
        <Field label="Manager PIN" value="" note="write it in — it is not stored in readable form" />
        <Field label="Quiz builder" value={`${stripScheme(playerUrl)}/admin`} note="admin token needed" />

        <h3 className="mt-8 text-lg font-bold">Players ask: how do I join?</h3>
        <p className="mt-1">{howToJoin}</p>

        {sheet.joinMode === 'TICKET' && (
          <>
            <h3 className="mt-8 text-lg font-bold">Taking money</h3>
            <ol className="mt-2 list-decimal space-y-1 pl-5">
              <li><b>Door</b> tab &rarr; <b>Cash {formatPence(sheet.entryFeePence)}</b> gives you a code to read out.</li>
              <li>Printing codes in advance? <b>Generate codes</b>, then tap <b>Sold</b> on one as you hand it over.</li>
              <li>A printed code does nothing until you mark it sold. That is deliberate.</li>
              <li><b>Comp</b> issues a free one. It is recorded as a comp, not as cash.</li>
            </ol>

            <h3 className="mt-8 text-lg font-bold">Refunds</h3>
            <p className="mt-1">
              Hand the money back first, then <b>Players</b> tab &rarr; find them &rarr;
              <b> Remove from quiz</b>. That takes them out of the game; it does not move
              any money, so record the refund separately with the <b>Refund</b> button.
            </p>
          </>
        )}

        <h3 className="mt-8 text-lg font-bold">If somebody is stuck</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>&ldquo;Name is taken&rdquo; &mdash; add a surname initial.</li>
          <li>Screen frozen &mdash; refresh. Their place and score are kept.</li>
          <li>Joined late &mdash; they play from the next question; earlier ones score nothing.</li>
          <li>No code letters O or I, and no digits 0 or 1. They are not in the alphabet.</li>
        </ul>
      </section>
    </Paper>
  );
}

const stripScheme = (url: string) => url.replace(/^https?:\/\//, '');

function Field({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="mt-5">
      <p className="text-sm uppercase tracking-widest text-neutral-500">{label}</p>
      {value ? (
        <p className="text-xl font-semibold break-all">{value}</p>
      ) : (
        <div className="mt-2 h-8 w-56 border-b-2 border-dashed border-neutral-400" />
      )}
      {note && <p className="mt-1 text-sm text-neutral-500">{note}</p>}
    </div>
  );
}

function Paper({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{`
        /* This page is for paper, so it ignores the app's dark palette. */
        html, body { background: #fff !important; color: #111 !important; }
        .page { page-break-after: always; padding: 0 0 2rem; }
        .page:last-child { page-break-after: auto; }
        @media print {
          .no-print { display: none !important; }
          @page { margin: 16mm; }
          html, body { font-size: 12pt; }
        }
      `}</style>
      <div className="mx-auto min-h-dvh max-w-3xl bg-white p-8 text-neutral-900">{children}</div>
    </>
  );
}
